<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MessagesApi from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useMessageContext } from './provider.js';
import Button from 'dashboard/components-next/button/Button.vue';

// The actions a bot reply draft was written as if they had gone through - the agent takes them
// before sending it. Sent by the bot in content_attributes.jarabot.assumed_actions as
// [{ id, name, identifiers }]: the bot's function name, worded here, and the order or claim numbers.
//
// An action with an id can be run from here: the bot carries out the call it stored for the draft
// and writes back status (running, then done or failed), the reference it produced (a claim number)
// and why it failed - see Messages::AssumedActionService. Drafts from before that have no id.
//
// A running action can be tried again as well: the bot may take a request and never answer, and the
// server alone decides whether a retry may start yet (it refuses one while the run is fresh).
//
// The bot decides again when the agent runs an action, so a done one may have gone otherwise than the
// draft was written for - a new claim filed because the one the draft updates was closed meanwhile. It
// then sends deviation (a code) and planned (what the draft names), and the draft's text needs fixing
// before it is sent: that is said in red right above it.
defineProps({
  actions: { type: Array, required: true },
});

const { t, te } = useI18n();
const store = useStore();
const { id: messageId, conversationId } = useMessageContext();

const confirmingId = ref(null);
const requestingId = ref(null);

// A robot's own function has no translation - its name reads well enough.
const actionLabel = name => {
  const action = String(name).toUpperCase();
  if (
    te(`CONVERSATION.COPILOT_ASSUMED_ACTIONS.ACTIONS.${action}`) ||
    te(`CONVERSATION.COPILOT_ASSUMED_ACTIONS.ACTIONS.${action}`, 'en')
  ) {
    return t(`CONVERSATION.COPILOT_ASSUMED_ACTIONS.ACTIONS.${action}`);
  }

  const words = String(name).replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const actionText = ({ name, identifiers }) => {
  const label = actionLabel(name);
  return identifiers?.length ? `${label} (${identifiers.join(', ')})` : label;
};

// idle, confirming, requesting (the click on its way), running, done or failed.
const actionState = action => {
  if (action.status === 'done') return 'done';
  if (requestingId.value === action.id) return 'requesting';
  if (confirmingId.value === action.id) return 'confirming';
  if (action.status === 'running') return 'running';
  return action.status === 'failed' ? 'failed' : 'idle';
};

const isBusy = action =>
  ['requesting', 'running'].includes(actionState(action));

// What a done action produced: a reference (a claim number), or a note on how it went through.
const doneText = action => {
  const notes = [t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.DONE')];
  if (action.reference) notes.push(action.reference);
  if (action.reason === 'claim_delayed') {
    notes.push(t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.CLAIM_DELAYED'));
  }
  return notes.join(' · ');
};

const hasDeviation = action =>
  actionState(action) === 'done' && Boolean(action.deviation);

// A code this version does not know yet still warns, just without the detail - and so does one that
// came without the claim it got, which the detailed wording would leave a hole for.
const deviationText = ({ deviation, reference, planned }) => {
  const params = { reference, planned };
  switch (reference ? deviation : null) {
    case 'claim_closed':
      return t(
        'CONVERSATION.COPILOT_ASSUMED_ACTIONS.DEVIATIONS.CLAIM_CLOSED',
        params
      );
    case 'new_claim':
      return t(
        'CONVERSATION.COPILOT_ASSUMED_ACTIONS.DEVIATIONS.NEW_CLAIM',
        params
      );
    case 'other_claim':
      return t(
        'CONVERSATION.COPILOT_ASSUMED_ACTIONS.DEVIATIONS.OTHER_CLAIM',
        params
      );
    case 'existing_claim':
      return t(
        'CONVERSATION.COPILOT_ASSUMED_ACTIONS.DEVIATIONS.EXISTING_CLAIM',
        params
      );
    default:
      return t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.DEVIATIONS.OTHER');
  }
};

const failureText = action => {
  const label =
    action.reason === 'unreachable'
      ? t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.UNREACHABLE')
      : t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.FAILED');
  return action.detail ? `${label}: ${action.detail}` : label;
};

const runAction = async action => {
  confirmingId.value = null;
  requestingId.value = action.id;
  try {
    // The response is the message with the action running - shown right away rather than when the
    // websocket brings it, which may be never with the cable down. The outcome comes that way later.
    const { data } = await MessagesApi.executeAssumedAction(
      conversationId.value,
      messageId.value,
      action.id
    );
    await store.dispatch('updateMessage', data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.REQUEST_FAILED')
    );
  } finally {
    requestingId.value = null;
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-1 px-3 py-2 text-xs rounded-lg bg-n-amber-4 text-n-amber-12"
    data-test-id="copilot-assumed-actions"
  >
    <span class="flex items-center gap-1.5 font-medium">
      <i class="i-lucide-list-checks size-3.5" />
      {{ t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.TITLE') }}
    </span>
    <ul class="flex flex-col gap-1 mb-0 list-none ltr:pl-0 rtl:pr-0">
      <li
        v-for="(action, index) in actions"
        :key="`${action.name}-${index}`"
        class="flex flex-wrap items-center gap-x-2 gap-y-1 min-h-6"
        :data-state="action.id ? actionState(action) : 'plain'"
      >
        <span class="flex items-center gap-1.5">
          <i
            v-if="action.id && hasDeviation(action)"
            class="i-lucide-triangle-alert size-3.5 text-n-ruby-11"
          />
          <i
            v-else-if="action.id && actionState(action) === 'done'"
            class="i-lucide-circle-check size-3.5 text-n-teal-11"
          />
          <i v-else class="i-lucide-dot size-3.5" />
          <span data-test-id="assumed-action-text">{{
            actionText(action)
          }}</span>
        </span>
        <template v-if="action.id">
          <template v-if="actionState(action) === 'done'">
            <span class="text-n-teal-11" data-test-id="assumed-action-outcome">
              {{ doneText(action) }}
            </span>
            <span
              v-if="hasDeviation(action)"
              class="basis-full font-medium text-n-ruby-11"
              data-test-id="assumed-action-deviation"
            >
              {{ deviationText(action) }}
            </span>
          </template>
          <span
            v-else-if="actionState(action) === 'confirming'"
            class="flex items-center gap-1"
          >
            {{ t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.CONFIRM') }}
            <Button
              xs
              color="amber"
              :label="t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.CONFIRM_YES')"
              data-test-id="assumed-action-confirm"
              @click="runAction(action)"
            />
            <Button
              xs
              ghost
              slate
              :label="t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.CONFIRM_NO')"
              @click="confirmingId = null"
            />
          </span>
          <template v-else>
            <span
              v-if="isBusy(action)"
              class="flex items-center gap-1 text-n-amber-11"
              data-test-id="assumed-action-outcome"
            >
              <i class="i-lucide-loader-circle size-3.5 animate-spin" />
              {{ t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.RUNNING') }}
            </span>
            <span
              v-else-if="actionState(action) === 'failed'"
              class="text-n-ruby-11"
              data-test-id="assumed-action-outcome"
            >
              {{ failureText(action) }}
            </span>
            <Button
              v-if="actionState(action) !== 'requesting'"
              xs
              outline
              color="amber"
              :label="
                actionState(action) === 'idle'
                  ? t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.EXECUTE')
                  : t('CONVERSATION.COPILOT_ASSUMED_ACTIONS.RETRY')
              "
              data-test-id="assumed-action-execute"
              @click="confirmingId = action.id"
            />
          </template>
        </template>
      </li>
    </ul>
  </div>
</template>
